import { useState, useCallback, useRef, useEffect } from "react";

const useAudioInteractionCheck = () => {
  const [needsInteraction, setNeedsInteraction] = useState(false);
  const pendingResolversRef = useRef([]);
  const howlerRef = useRef(null);
  const hasUserGestureRef = useRef(false);
  const inFlightPromiseRef = useRef(null);

  // Track the very first user gesture on the page.

  useEffect(() => {
    const markGesture = () => {
      hasUserGestureRef.current = true;
    };

    const opts = { capture: true, once: true };

    window.addEventListener("pointerdown", markGesture, opts);
    window.addEventListener("keydown", markGesture, opts);
    window.addEventListener("touchstart", markGesture, opts);

    return () => {
      window.removeEventListener("pointerdown", markGesture, opts);
      window.removeEventListener("keydown", markGesture, opts);
      window.removeEventListener("touchstart", markGesture, opts);
    };
  }, []);

  // Lazy load Howler to delay AudioContext initialization
  const initHowler = useCallback(async () => {
    if (!howlerRef.current) {
      const { Howler } = await import("howler");
      howlerRef.current = Howler;
    }
  }, []);

  const ensureAudioReady = useCallback(async () => {
    await initHowler();
    const howler = howlerRef.current;
    const ctx = howler?.ctx;

    if (!ctx) return true;

    if (ctx.state === "running") return true;

    if (inFlightPromiseRef.current) return inFlightPromiseRef.current;

    try {
      await Promise.race([
        ctx.resume(),
        new Promise((resolve) => setTimeout(resolve, 300)),
      ]);
    } catch {
      // Ignore resume errors
    }

    // If resume succeeded, proceed
    if (ctx.state === "running") return true;

    // If the user has already interacted, any remaining suspension

    if (hasUserGestureRef.current) return true;

    setNeedsInteraction(true);

    const promise = new Promise((resolve) => {
      pendingResolversRef.current.push(resolve);
    });

    inFlightPromiseRef.current = promise;

    // Clean up the in-flight ref once resolved
    promise.then(() => {
      inFlightPromiseRef.current = null;
    });

    return promise;
  }, [initHowler]);

  // Handle user interaction to resume audio
  const handleUserInteraction = useCallback(async () => {
    try {
      await initHowler();
      const howler = howlerRef.current;
      const ctx = howler?.ctx;

      if (ctx && ctx.state === "suspended") {
        await ctx.resume();
      }

      // Record that the user has interacted
      hasUserGestureRef.current = true;

      setNeedsInteraction(false);

      // Resolve all pending ensureAudioReady callers
      pendingResolversRef.current.forEach((resolve) => resolve(true));
      pendingResolversRef.current = [];
      inFlightPromiseRef.current = null;
    } catch (err) {
      console.error("Failed to handle user interaction:", err);
    }
  }, [initHowler]);

  return {
    needsInteraction,
    handleUserInteraction,
    ensureAudioReady,
  };
};

export default useAudioInteractionCheck;
